import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { clearAnalyticsIds, trackPageview } from "@/lib/analytics/track";
import { updateGoogleAnalyticsConsent } from "@/lib/analytics/ga";
import { loadPlausible, loadPlausibleIfConsented } from "@/lib/analytics/plausible";

type Choice = "all" | "essential";
const KEY = "fenny_consent";
export const CONSENT_REOPEN_EVENT = "fenny:open-consent";

/** Öppnar cookie-bannern igen (används av sidfotens "Cookieinställningar"). */
export function openConsentSettings() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CONSENT_REOPEN_EVENT));
}

export function ConsentBanner() {
  const { t } = useTranslation();
  const [choice, setChoice] = useState<Choice | null>(null);
  const [mounted, setMounted] = useState(false);
  const [reopened, setReopened] = useState(false);

  useEffect(() => {
    setMounted(true);
    try {
      const v = localStorage.getItem(KEY) as Choice | null;
      setChoice(v);
    } catch {}
    // Tidigare samtycke → starta mätningen som besökaren redan godkänt.
    loadPlausibleIfConsented();

    const onReopen = () => setReopened(true);
    window.addEventListener(CONSENT_REOPEN_EVENT, onReopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, onReopen);
  }, []);

  function decide(c: Choice) {
    try {
      localStorage.setItem(KEY, c);
      // 1 år giltigt
      document.cookie = `${KEY}=${c}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
    } catch {}
    setChoice(c);
    setReopened(false);
    updateGoogleAnalyticsConsent(c);
    if (c === "all") {
      loadPlausible();
      trackPageview();
    } else {
      clearAnalyticsIds();
    }
  }

  if (!mounted || (choice !== null && !reopened)) return null;


  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("consent.ariaLabel")}
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-3xl rounded-2xl border border-white/10 bg-black/85 p-4 backdrop-blur-md shadow-2xl sm:p-5"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-white/85">
          <p className="font-medium text-white">{t("consent.title")}</p>
          <p className="mt-1 text-white/70">
            {t("consent.body")}{" "}
            <a href="/integritet" className="underline underline-offset-4 hover:text-white">
              {t("consent.readMore")}
            </a>
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            onClick={() => decide("essential")}
            className="rounded-full border border-white/20 px-4 py-2 text-xs font-medium text-white/90 hover:bg-white/5"
          >
            {t("consent.essentialOnly")}
          </button>
          <button
            onClick={() => decide("all")}
            className="rounded-full bg-mint px-4 py-2 text-xs font-medium text-mint-foreground hover:opacity-90"
          >
            {t("consent.acceptAll")}
          </button>
        </div>
      </div>
    </div>
  );
}
